import { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle2, Phone, Mail, Loader2 } from 'lucide-react';
import PublicAddressAutocomplete from '@/components/public/PublicAddressAutocomplete';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { value: 'new_construction', label: 'New Well Construction' },
  { value: 'pump_service', label: 'Pump Service' },
  { value: 'site_inspection', label: 'Site Inspection' },
  { value: 'filters_service', label: 'Filters & Service' },
];

const COUNTIES = [
  'Highlands', 'Polk', 'Hardee', 'DeSoto', 'Okeechobee', 'Charlotte',
  'Sarasota', 'Manatee', 'Hillsborough', 'Pasco', 'Hernando', 'Citrus',
  'Sumter', 'Lake', 'Marion', 'Levy', 'Pinellas',
];

const SOURCES = ['Google', 'Referral', 'Social Media', 'Road Sign', 'Other'];

const formatPhone = (value) => {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (!d) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const baseInput =
  'w-full h-12 px-4 rounded-xl bg-[#0B1220]/80 border text-white placeholder:text-zinc-500 text-sm outline-none transition-all duration-200 focus:ring-2';
const okBorder = 'border-white/[0.1] focus:border-[#1FB6E9] focus:ring-[#1FB6E9]/25 focus:shadow-[0_0_24px_-4px_rgba(31,182,233,0.5)]';
const errBorder = 'border-red-500/70 focus:border-red-500 focus:ring-red-500/25';
const inputCls = (hasErr) => `${baseInput} ${hasErr ? errBorder : okBorder}`;
const labelCls = 'block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2';
const FieldError = ({ msg }) => msg ? <p className="text-red-400 text-xs mt-1.5">{msg}</p> : null;

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '',
  service_type: 'new_construction', address: '', city: '', county: '',
  state: 'FL', zip_code: '', preferred_date: '', preferred_day: 'asap', how_heard: '', description: '',
};

export const BookOnlineForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [bookingNumber, setBookingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [addressVerified, setAddressVerified] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // D2 (Alian): a contractor filling this out for multiple Rebuild Florida
  // properties must be able to submit again right away — bumping this key
  // forces a clean remount of the address autocomplete (clears its internal
  // "verified" state too) instead of requiring a page refresh.
  const [formKey, setFormKey] = useState(0);

  const resetForFreshSubmission = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setError('');
    setAddressVerified(false);
    setBookingNumber('');
    setSubmitted(false);
    setFormKey((k) => k + 1);
  };

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const handleAddress = (r) => {
    setAddressVerified(!!r.verified);
    setErrors((p) => ({ ...p, address: undefined }));
    setForm((p) => ({
      ...p,
      address: r.address ?? p.address,
      // Clear stale city/county/state/zip from a PREVIOUS selection while
      // the user is still typing a new address (r.verified is only true
      // right after an actual dropdown pick in PublicAddressAutocomplete).
      city: r.verified ? (r.city ?? '') : '',
      county: r.verified ? (r.county ?? '') : '',
      state: r.verified ? (r.state ?? '') : '',
      zip_code: r.verified ? (r.zip_code ?? '') : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required.';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required.';
    if (!form.phone.trim()) errs.phone = 'Phone number is required.';
    if (!form.address.trim()) errs.address = 'Please enter your service address.';
    else if (!addressVerified) errs.address = 'Please verify this address — select it from the suggestions.';

    if (Object.keys(errs).length) {
      setErrors(errs);
      setError('Please complete the highlighted fields before submitting.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/bookings/public`, form);
      setBookingNumber(res.data.booking_number);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please call us at (863) 658-0035.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6 sm:p-9 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#1FB6E9]/15 blur-[90px] pointer-events-none" />
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            data-testid="book-success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center py-6 relative z-10"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.1 }}
              className="w-16 h-16 rounded-full bg-[#1FB6E9]/15 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle2 className="w-9 h-9 text-[#38BDF8]" />
            </motion.div>
            <h3 className="text-2xl font-bold text-white mb-2 font-display">Request received</h3>
            <p className="text-zinc-400 mb-6">Thank you! Our office will contact you shortly to confirm your appointment.</p>
            <div className="inline-block rounded-xl bg-[#0B1220] border border-white/10 px-6 py-4 mb-6">
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Reference number</p>
              <p className="text-2xl font-bold text-[#38BDF8] font-mono" data-testid="book-ref-number">{bookingNumber}</p>
            </div>
            <button
              type="button"
              data-testid="book-submit-another-btn"
              onClick={resetForFreshSubmission}
              className="mb-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[#1FB6E9]/40 text-[#38BDF8] font-semibold text-sm hover:bg-[#1FB6E9]/10 transition-colors"
            >
              <Send className="w-4 h-4" /> Submit another request
            </button>
            <div className="flex items-center justify-center gap-6 text-sm text-zinc-400">
              <a href="tel:+18636580035" className="flex items-center gap-1.5 hover:text-[#38BDF8] transition-colors"><Phone className="w-4 h-4" /> (863) 658-0035</a>
              <a href="mailto:info@bluewell.pro" className="flex items-center gap-1.5 hover:text-[#38BDF8] transition-colors"><Mail className="w-4 h-4" /> Email us</a>
            </div>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            noValidate
            className="space-y-5 relative z-10"
            data-testid="book-form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {error && (
              <div data-testid="book-error" className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/25 text-red-300 text-sm">{error}</div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>First Name *</label>
                <input data-testid="book-first-name" className={inputCls(errors.first_name)} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="John" />
                <FieldError msg={errors.first_name} />
              </div>
              <div>
                <label className={labelCls}>Last Name *</label>
                <input data-testid="book-last-name" className={inputCls(errors.last_name)} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Doe" />
                <FieldError msg={errors.last_name} />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input data-testid="book-phone" className={inputCls(errors.phone)} value={form.phone} onChange={(e) => set('phone', formatPhone(e.target.value))} placeholder="(863) 658-0035" />
                <FieldError msg={errors.phone} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="book-email" type="email" className={inputCls(false)} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@email.com" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Service Type</label>
                <select data-testid="book-service-type" className={`${inputCls(false)} appearance-none`} value={form.service_type} onChange={(e) => set('service_type', e.target.value)}>
                  {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Preferred Day</label>
                <div className="grid grid-cols-3 gap-2" data-testid="book-preferred-day">
                  {[
                    { v: 'asap', label: 'ASAP', sub: 'Within 24h' },
                    { v: 'any_day', label: 'Any Day', sub: 'Within 48h' },
                    { v: 'specific', label: 'Pick Date', sub: 'Choose' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`book-preferred-day-${o.v}`}
                      onClick={() => set('preferred_day', o.v)}
                      className={`h-12 rounded-xl border text-center transition-all duration-200 ${form.preferred_day === o.v ? 'border-[#1FB6E9] bg-[#1FB6E9]/15 text-white shadow-[0_0_18px_-6px_rgba(31,182,233,0.6)]' : 'border-white/[0.1] bg-[#0B1220]/80 text-zinc-400 hover:border-white/25'}`}
                    >
                      <span className="block text-sm font-semibold leading-none">{o.label}</span>
                      <span className="block text-[10px] mt-0.5 opacity-70">{o.sub}</span>
                    </button>
                  ))}
                </div>
                {form.preferred_day === 'specific' && (
                  <input data-testid="book-date" type="date" className={`${inputCls(false)} mt-2`} value={form.preferred_date} onChange={(e) => set('preferred_date', e.target.value)} />
                )}
              </div>
            </div>

            <div>
              <label className={labelCls}>Service Address *</label>
              <PublicAddressAutocomplete key={formKey} value={form.address} onChange={handleAddress} error={!!errors.address} />
              <FieldError msg={errors.address} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>City</label>
                <input data-testid="book-city" className={inputCls(false)} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Sebring" />
              </div>
              <div>
                <label className={labelCls}>County</label>
                <select data-testid="book-county" className={`${inputCls(false)} appearance-none`} value={form.county} onChange={(e) => set('county', e.target.value)}>
                  <option value="">Select…</option>
                  {COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>ZIP Code</label>
                <input data-testid="book-zip" className={inputCls(false)} value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} placeholder="33870" />
              </div>
            </div>

            <div>
              <label className={labelCls}>How did you hear about us?</label>
              <select data-testid="book-how-heard" className={`${inputCls(false)} appearance-none`} value={form.how_heard} onChange={(e) => set('how_heard', e.target.value)}>
                <option value="">Select…</option>
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label className={labelCls}>Tell us about your project</label>
              <textarea data-testid="book-description" rows={3} className={`${inputCls(false)} h-auto py-3 resize-none`} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe what you need…" />
            </div>

            <button
              data-testid="book-submit-btn"
              type="submit"
              disabled={loading}
              className="cta-shimmer w-full py-4 rounded-xl bg-[#1FB6E9] hover:bg-[#38BDF8] text-[#06121f] font-bold text-base flex items-center justify-center gap-2 shadow-[0_12px_34px_-8px_rgba(31,182,233,0.7)] hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-200 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Request My Appointment</>}
            </button>
            <p className="text-center text-xs text-zinc-500">No spam — we'll call to confirm your appointment.</p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookOnlineForm;
